// Imports remain unchanged
import { useCallback, useEffect, useRef, useState } from "react";
import { httpGet, httpPost } from "../../../modules/Backend";
import { GroupedVirtuoso } from "react-virtuoso";
import { useParams } from "react-router";
import Pill from "../Pill";
import { movingSidePanelSignal, staticSidePanelSignal } from "../layout/Layout";
import { useSignals } from "@preact/signals-react/runtime";
import { StatusDto } from "../../../enums/Domain/Status";
import { EntityNames } from "../../../enums/EntityNames";
import SidePanelDisposalPickListItem from "../layout/SidePanel/layouts/SidePanelDisposalPickListItem";
import SidePanelDisposalPickList, { IDisposalCheck } from "../layout/SidePanel/layouts/SidePanelDisposalPickList";
import IconChevronUp from "../../../assets/icons/icon-chevron-up-blue.svg";
import IconChevronDown from "../../../assets/icons/icon-chevron-down-blue.svg";
import IconTickGreen from "../../../assets/icons/icon-green-tick-circle.svg";
import IconTickGrey from "../../../assets/icons/icon-grey-tick-circle.svg";
import IconFullPallet from "../../../assets/icons/icon-full-pallet.svg";
import IconCrossGrey from "../../../assets/icons/icon-cross-grey.svg";
import IconCrossBlue from "../../../assets/icons/icon-cross-blue.svg";
import IconCheckedGreen from "../../../assets/icons/icon-checked-green.svg";
import CheckIcon from "../../../assets/icons/icon-check-white.svg";
import { Checkbox } from "react-aria-components";
import { DisposalLineItemStatusSmartEnum } from "../../../enums/Domain/DisposalLineItemStatusSmartEnum";
import { PropertyNames } from "../../../enums/PropertyNames";
import { FilterMatchType } from "../../../enums/FilterMatchType";
import { PropertyTypes } from "../../../enums/PropertyTypes";
import useDisposalPickListActions from "../picking/useDisposalPickListActions";
import LoadingButton from "../LoadingButton";
import ReactDOM from "react-dom";
import DisposalPickingButtons from "../picking/DisposalPickingButtons";

export interface IPickList {
  disposalId: string;
  items: IPicklistItem[];
  minimumDisposalCheckCount: number;
  maxBoxCountForOnDemandDisposal: number,
  currentDisposalChecks: IDisposalCheck[];
  onDisposalCheckAdded: (item: IDisposalCheck) => void,
  onDisposalCheckRemoved: (item: IDisposalCheck) => void
}

export interface IPicklistItem {
  id: string;
  breadcrumb: string;
  reference: string;
  volume: string,
  materialTypeName: string;
  packingBoxId: string,
  sequenceNumber: number,
  years: string;
  hasBeenChecked: boolean,
  couldWholeOuterCubeBeDisposed: boolean,
  status: StatusDto;
  outerCubeType: StatusDto;
  notes: string;
}

type UseCase = 'cancel' | 'uncancel' | 'check' | 'uncheck';

type LoadingState = Record<UseCase, boolean>;

const initialLoadingState: LoadingState = {
  cancel: false,
  uncancel: false,
  check: false,
  uncheck: false,
};

export default function DisposalsPickList() {
  useSignals();

  const { disposalId } = useParams();
  const [loading, setLoading] = useState<LoadingState>(initialLoadingState);
  const [lineItems, setLineItems] = useState<IPicklistItem[]>([]);
  const [groupedData, setGroupedData] = useState<{ breadcrumb: string; items: IPicklistItem[] }[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [initialGroupCollapse, setInitialGroupCollapse] = useState<boolean>(true);
  const [viewedItem, setViewedItem] = useState<string>();
  const [minimumDisposalCheckCount, setMinimumDisposalCheckCount] = useState<number>(0);
  const [maxBoxCountForOnDemandDisposal, setMaxBoxCountForOnDemandDisposal] = useState<number>(0);
  const [currentDisposalChecks, setCurrentDisposalChecks] = useState<IDisposalCheck[]>([]);
  const [selections, setSelections] = useState<Set<string>>(new Set());
  const list = useRef<any>(null);

  const { save, submit, cancel } = useDisposalPickListActions({
    disposalId,
    items: lineItems,
    maxBoxCountForOnDemandDisposal
  });

  const setLoadingFor = useCallback((key: UseCase, value: boolean) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  }, []);

  const getLoading = useCallback((key: UseCase): boolean => {
    return loading[key];
  }, [loading]);

  const hasAnyLoading = useCallback((): boolean => {
    return Object.values(loading).some(v => v);
  }, [loading]);

  const groupByBreadcrumb = (items: IPicklistItem[]) => {
    const grouped = items.reduce((acc: any, item: IPicklistItem) => {
      const group = acc.find((g: any) => g.breadcrumb === item.breadcrumb);
      if (group) {
        group.items.push(item);
      } else {
        acc.push({ breadcrumb: item.breadcrumb, items: [item] });
      }
      return acc;
    }, []);

    grouped.forEach((group: any) => {
      group.items.sort((a: IPicklistItem, b: IPicklistItem) => a.sequenceNumber - b.sequenceNumber);
    });

    return grouped;
  };

  const toggleGroup = (index: number) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleSelectGroup = (index: number) => {
    const groupItems = groupedData[index].items;
    const allChecked = groupItems.every(item => selections.has(item.id));

    const updatedSelections = new Set(selections);

    if (allChecked) {
      for (const item of groupItems) {
        updatedSelections.delete(item.id);
      }
    } else {
      for (const item of groupItems) {
        updatedSelections.add(item.id);
      }
    }
    setSelections(updatedSelections);
  };

  const visibleGroupCounts = groupedData.map((group, index) =>
    collapsedGroups.has(index) ? 0 : group.items.length
  );

  const flattenedItems = groupedData.flatMap((group, index) =>
    collapsedGroups.has(index) ? [] : group.items
  );

  async function getData() {
    const timeout = setTimeout(() => setShowLoader(true), 200);
    const disposalResult = await httpGet(`api/${EntityNames.disposals}/getPickList?disposalId=${disposalId}`);

    const filters = [
      {
        propertyName: PropertyNames.id,
        value: disposalId,
        required: false,
        matchType: FilterMatchType.Equal,
        type: PropertyTypes.uuid,
      }
    ]

    const requestPayload = {
      filters: [...filters],
      take: undefined,
      keysets: [],
    };
    const disposalChecksResult = await httpPost(`api/${EntityNames.disposalChecks}/list`, requestPayload);
    renderEmptyList();

    if (disposalId && disposalResult?.data && disposalChecksResult?.data) {
      setLineItems(disposalResult?.data.items);
      setMinimumDisposalCheckCount(disposalResult?.data.minimumDisposalCheckCount);
      setMaxBoxCountForOnDemandDisposal(disposalResult?.data.maxBoxCountForOnDemandDisposal);
      setCurrentDisposalChecks(disposalChecksResult.data[EntityNames.disposalChecks]);
      staticSidePanelSignal.value = <SidePanelDisposalPickList disposalId={disposalId}
        items={disposalResult?.data.items}
        minimumDisposalCheckCount={disposalResult?.data.minimumDisposalCheckCount}
        maxBoxCountForOnDemandDisposal={disposalResult?.data.maxBoxCountForOnDemandDisposal}
        currentDisposalChecks={currentDisposalChecks}
        onDisposalCheckAdded={onDisposalCheckAdded}
        onDisposalCheckRemoved={onDisposalCheckRemoved} />;
    }
    setShowLoader(false);
    clearTimeout(timeout);
  }

  function onDisposalCheckAdded(disposalCheck: IDisposalCheck) {
    setCurrentDisposalChecks([...currentDisposalChecks, disposalCheck]);
  }

  function onDisposalCheckRemoved(disposalCheck: IDisposalCheck) {
    const updated = currentDisposalChecks.filter(item => item.id !== disposalCheck.id);
    setCurrentDisposalChecks(updated);
  }

  const handleChange = (input: string) => (id: string, value: string | number | boolean | StatusDto) =>
    setLineItems((prevData: any) =>
      prevData.map((item: any) => (item.id === id ? { ...item, [input]: value } : item))
    );

  const handleSave = useCallback(async () => {
    await save();
  }, [save]);

  const handleSubmit = useCallback(async () => {
    await submit();
  }, [submit]);

  function viewItem(lineItem: any) {
    movingSidePanelSignal.value = {
      show: true,
      id: lineItem.id,
      component: (
        <SidePanelDisposalPickListItem
          key={lineItem?.id}
          disposalId={disposalId}
          selectedItem={lineItem}
          items={lineItems}
          handleChange={handleChange}
          minimumDisposalCheckCount={minimumDisposalCheckCount}
          maxBoxCountForOnDemandDisposal={maxBoxCountForOnDemandDisposal}
          currentDisposalChecks={currentDisposalChecks}
          onSave={handleSave}
          onSubmit={handleSubmit}
        />
      ),
    };
  }

  function handleHasBeenCheckedChanged(id: string, checked: boolean) {
    const updatedSet = new Set(selections);
    if (checked) {
      updatedSet.add(id);
    } else {
      updatedSet.delete(id);
    }
    setSelections(updatedSet);
  }

  function clearSelections() {
    setSelections(new Set());
  }

  async function handleCancelBoxes() {
    setLoadingFor('cancel', true);
    await handleUpdateSelections(true, () => new StatusDto(DisposalLineItemStatusSmartEnum.Cancelled.value, DisposalLineItemStatusSmartEnum.getName()));
    setLoadingFor('cancel', false);
  }

  function countBoxesThatWouldCancelled() {
    let count = 0;
    for (const lineItem of lineItems) {
      if (selections.has(lineItem.id) && lineItem.status.value !== DisposalLineItemStatusSmartEnum.Cancelled.value) {
        count++;
      }
    }
    return count;
  }

  async function handleCheckBoxes() {
    setLoadingFor('check', true);
    await handleUpdateSelections(true);
    setLoadingFor('check', false);
  }

  function countBoxesThatWouldBeChecked() {
    let count = 0;
    for (const lineItem of lineItems) {
      if (selections.has(lineItem.id) && lineItem.hasBeenChecked === false) {
        count++;
      }
    }
    return count;
  }

  async function handleUncancelBoxes() {
    setLoadingFor('uncancel', true);
    await handleUpdateSelections(undefined, () => new StatusDto(DisposalLineItemStatusSmartEnum.ToPick.value, DisposalLineItemStatusSmartEnum.getName()));
    setLoadingFor('uncancel', false);
  }

  function countBoxesThatWouldBeUncancelled() {
    let count = 0;
    for (const lineItem of lineItems) {
      if (selections.has(lineItem.id) && lineItem.status.value === DisposalLineItemStatusSmartEnum.Cancelled.value) {
        count++;
      }
    }
    return count;
  }

  async function handleUncheckBoxes() {
    setLoadingFor('uncheck', true);
    await handleUpdateSelections(false);
    setLoadingFor('uncheck', false);
  }

  function countBoxesThatWouldBeUnchecked() {
    let count = 0;
    for (const lineItem of lineItems) {
      if (selections.has(lineItem.id) && lineItem.hasBeenChecked === true) {
        count++;
      }
    }
    return count;
  }

  async function handleUpdateSelections(hasBeenChecked?: boolean | undefined, statusFunc?: () => StatusDto) {
    if (selections.size === 0) {
      return;
    }

    const updatedPickList = lineItems.map(item => {
      if (selections.has(item.id)) {
        return {
          ...item, // shallow copy
          ...(hasBeenChecked !== undefined && { hasBeenChecked }),
          ...(statusFunc && { status: statusFunc() }),
        };
      }
      return item;
    });

    setLineItems(updatedPickList);
  }

  function getCheckSummary(groupIndex: number) {
    const amountChecked = groupedData[groupIndex].items.filter(i => i.hasBeenChecked).length;
    const totalAmount = groupedData[groupIndex].items.length;
    return <p className="pick-list__check-count"><span className="fontWeight--bold">{amountChecked}</span>/{totalAmount}</p>
  }

  function hasWholeGroupBeenSelected(groupIndex: number) {
    const idsInGroup = groupedData[groupIndex].items.map(x => x.id);
    return idsInGroup.every(x => selections.has(x));
  }

  function hasWholeGroupBeenChecked(groupIndex: number) {
    const amountChecked = groupedData[groupIndex].items.filter(i => i.hasBeenChecked).length;
    const totalAmount = groupedData[groupIndex].items.length;
    return amountChecked === totalAmount;
  }

  function couldWholePalletBeDisposed(groupIndex: number) {
    return groupedData[groupIndex].items.every(i => i.couldWholeOuterCubeBeDisposed && i.status.value === DisposalLineItemStatusSmartEnum.ToPick.value);
  }

  function getPalletType(groupIndex: number) {
    const sampleBox = groupedData[groupIndex]?.items[0];
    return sampleBox.outerCubeType;
  }

  function getBoxDescriptor(count: number) {
    return count === 1 ? 'box' : 'boxes';
  }

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (lineItems.length > 0) {
      const grouped = groupByBreadcrumb(lineItems);
      setGroupedData(grouped);

      if (initialGroupCollapse) {
        const allGroupIndexes = new Set<number>(grouped.map((_: any, index: number) => index));
        setCollapsedGroups(allGroupIndexes);
        setInitialGroupCollapse(false);
      }
    }
    if (viewedItem) {
      viewItem(lineItems.find((x: any) => x.id == viewedItem));
    }
    staticSidePanelSignal.value = <SidePanelDisposalPickList
      disposalId={disposalId!}
      items={lineItems}
      minimumDisposalCheckCount={minimumDisposalCheckCount}
      currentDisposalChecks={currentDisposalChecks}
      maxBoxCountForOnDemandDisposal={maxBoxCountForOnDemandDisposal}
      onDisposalCheckAdded={onDisposalCheckAdded}
      onDisposalCheckRemoved={onDisposalCheckRemoved} />;
  }, [lineItems, currentDisposalChecks, minimumDisposalCheckCount, maxBoxCountForOnDemandDisposal, disposalId, viewedItem, initialGroupCollapse]);

  // Separate effect for currentDisposalChecks
  useEffect(() => {
    staticSidePanelSignal.value = <SidePanelDisposalPickList
      disposalId={disposalId!}
      items={lineItems}
      minimumDisposalCheckCount={minimumDisposalCheckCount}
      currentDisposalChecks={currentDisposalChecks}
      maxBoxCountForOnDemandDisposal={maxBoxCountForOnDemandDisposal}
      onDisposalCheckAdded={onDisposalCheckAdded}
      onDisposalCheckRemoved={onDisposalCheckRemoved} />;
  }, [currentDisposalChecks]);

  useEffect(() => {
    if (movingSidePanelSignal.value.id && movingSidePanelSignal.value.show) {
      setViewedItem(movingSidePanelSignal.value.id);
    } else setViewedItem("");
  }, [movingSidePanelSignal.value]);

  function renderEmptyList() {
    return (
      <div>
        {showLoader && lineItems.length == 0 &&
          <div className="react-aria-Table">
            <table>
              <th><p className="small">Packing Box</p></th>
              <th><p className="small">Sequence No</p></th>
              <th><p className="small">Years</p></th>
              <th><p className="small">Contains</p></th>
              <th><p className="small">Status</p></th>
              {[...Array(5)].map((e, i) => (
                <tr key={i}>
                  {[...Array(5)].map((e, j) => (
                    <td key={j}>
                      <div className="loaders loaders__list"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </table>
          </div>
        }
      </div>
    );
  }

  return (
    <>
      {renderEmptyList()}
      {!showLoader &&
        <div className="height--full list__wrapper">
          <div className="pick-list__action-bar">
            <div className="pick-list__clear-selection">
              <p className="small">{selections.size} {getBoxDescriptor(selections.size)} selected</p>
              {selections.size > 0 &&
                <div className="pick-list__clear-button" onClick={clearSelections}>
                  <img src={IconCrossBlue} />
                  <p>Clear</p>
                </div>
              }
            </div>
            <div className="pick-list__action-button-set">
              {selections.size > 0 &&
                <>
                  {countBoxesThatWouldCancelled() > 0 &&
                    <LoadingButton className={"button button--extra-small button--primary--destructive"}
                      isDisabled={selections.size === 0 || hasAnyLoading()}
                      onPress={handleCancelBoxes}
                      isLoading={getLoading('cancel')}
                      loadingText="Saving"
                      buttonText={`Cancel ${countBoxesThatWouldCancelled()} ${getBoxDescriptor(countBoxesThatWouldCancelled())}`}>
                    </LoadingButton>
                  }
                  {countBoxesThatWouldBeUncancelled() > 0 &&
                    <LoadingButton className={"button button--extra-small button--secondary--destructive"}
                      isDisabled={selections.size === 0 || hasAnyLoading()}
                      onPress={handleUncancelBoxes}
                      isLoading={getLoading('uncancel')}
                      loadingText="Saving"
                      buttonText={`Uncancel ${countBoxesThatWouldBeUncancelled()} ${getBoxDescriptor(countBoxesThatWouldBeUncancelled())}`}>
                    </LoadingButton>
                  }
                  {countBoxesThatWouldBeChecked() > 0 &&
                    <LoadingButton className={"button button--extra-small button--primary"}
                      isDisabled={selections.size === 0 || hasAnyLoading()}
                      onPress={handleCheckBoxes}
                      isLoading={getLoading('check')}
                      loadingText="Saving"
                      buttonText={`Verify ${countBoxesThatWouldBeChecked()} ${getBoxDescriptor(countBoxesThatWouldBeChecked())}`}>
                    </LoadingButton>
                  }
                  {countBoxesThatWouldBeUnchecked() > 0 &&
                    <LoadingButton className={"button button--extra-small button--secondary"}
                      isDisabled={selections.size === 0 || hasAnyLoading()}
                      onPress={handleUncheckBoxes}
                      isLoading={getLoading('uncheck')}
                      loadingText="Saving"
                      buttonText={`Unverify ${countBoxesThatWouldBeUnchecked()} ${getBoxDescriptor(countBoxesThatWouldBeUnchecked())}`}>
                    </LoadingButton>
                  }
                </>
              }
            </div>
          </div>
          <div className="grid-x padding--xxs alignItems--center">
            <p className="small small-4">Box</p>
            <p className="small small-2">Sequence</p>
            <p className="small small-1">Volume</p>
            <p className="small small-2">Years</p>
            <p className="small small-2">Status</p>
            <p className="small small-1"></p>
          </div>
          <GroupedVirtuoso
            groupCounts={visibleGroupCounts}
            groupContent={(index) => (
              <div style={{ zIndex: 10 }} onClick={() => toggleGroup(index)} className="pick-list__group-header-wrapper padding__bottom--xxs">
                <div className="pick-list__group-header-section">
                  <div className={`pick-list__group-header`}>
                    <div className="display--flex gap--xxs alignItems--center">
                      <Checkbox
                        isSelected={hasWholeGroupBeenSelected(index)}
                        onChange={() => toggleSelectGroup(index)}>
                        <div className={`input-checkbox ${hasWholeGroupBeenSelected(index) ? "input-checkbox--selected" : "input-checkbox--unselected"}`}>
                          {hasWholeGroupBeenSelected(index) && <img alt="" src={CheckIcon} />}
                        </div>
                      </Checkbox>
                      <h2 className="pick-list__group-heading-text">{groupedData[index].breadcrumb}</h2>
                    </div>
                    <div className="display--flex alignItems--center">
                      {couldWholePalletBeDisposed(index) && <img className="margin__right--xxs" src={IconFullPallet} />}
                      <div className="margin__right--xxs">
                        <Pill statusDto={getPalletType(index)} />
                      </div>
                    </div>
                  </div>
                  <div className="pick-list__group-widget">
                    <div className="display--flex gap--xxxs alignItems--center">
                      <img src={`${hasWholeGroupBeenChecked(index) ? IconTickGreen : IconTickGrey}`} />
                      {getCheckSummary(index)}
                    </div>
                    <img src={`${collapsedGroups.has(index) ? IconChevronDown : IconChevronUp}`} />
                  </div>
                </div>
              </div>
            )}
            ref={list}
            itemContent={(index) => {
              const lineItem = flattenedItems[index];
              if (!lineItem) return null;

              return (
                <div
                  className="pick-list__item"
                  onClick={() => viewItem(lineItem)}
                  data-selected={viewedItem === lineItem.id}
                >
                  <div className="grid-x padding--xxs alignItems--center">
                    <div className="small-4 display--flex gap--xxs alignItems--center">
                      <Checkbox
                        isSelected={selections.has(lineItem.id)}
                        onChange={(val) => handleHasBeenCheckedChanged(lineItem.id, val)}>
                        <div className={`input-checkbox ${selections.has(lineItem.id) ? "input-checkbox--selected" : "input-checkbox--unselected"}`}>
                          <img alt="" src={CheckIcon} />
                        </div>
                      </Checkbox>
                      <p className="textwrap">{lineItem.reference}</p>
                    </div>
                    <div className="small-2">
                      <p className="textwrap">{lineItem.sequenceNumber}</p>
                    </div>
                    <div className="small-1">
                      <p className="textwrap">{lineItem.volume}</p>
                    </div>
                    <div className="small-2">
                      <p className="textwrap">{lineItem.years}</p>
                    </div>
                    <div className="small-2">
                      <Pill statusDto={lineItem.status} />
                    </div>
                    <div className="small-1 pick-list__item-check">
                      <div className="display--flex gap--xxxs alignItems--center justifyContent--center">
                        <img src={lineItem.hasBeenChecked ? IconCheckedGreen : IconCrossGrey} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      }
    </>
  );
}
